import { type PasteOrDropPayload } from '@canvas/clipboard/types';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';

import { getInputTarget } from '../input-target/get-input-target';
import {
  type Capture,
  type CaptureListener,
  type CaptureType,
  type IdCallback,
} from '../input-types';

/**
 * Adds event listeners and massages our event data, adding the resulting data to a stream.
 * Returns a cleanup function for removing event listeners.
 */
export const setupDragEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  onCapture: (capture: Capture) => void,
  captureContext: {
    x?: number;
    y?: number;
  },
  listeners: Map<CaptureType, CaptureListener>,
): (() => void) => {
  // Needed to prevent the default behaviour of opening the file being dropped
  const onFileDragOver = (evt: DragEvent) => {
    evt.preventDefault();
  };

  const onDropCapture = (
    evt: Event,
    dropPayload: PasteOrDropPayload,
    position: { x: number; y: number },
  ) => {
    onCapture({
      id: id(),
      type: 'paste',
      target: getInputTarget(evt.target),
      properties: {
        clipboardContent: dropPayload,
        x: position.x,
        y: position.y,
      },
    });
  };

  const onFileDrop = (evt: DragEvent) => {
    if (evt.dataTransfer == null) {
      return;
    }

    evt.preventDefault();

    listeners
      .get('drop')?.(evt)
      .then((dropPayload: PasteOrDropPayload | undefined) => {
        if (dropPayload) {
          onDropCapture(evt, dropPayload, { x: evt.clientX, y: evt.clientY });
        }
      });
  };

  root.addEventListener('dragover', onFileDragOver);
  root.addEventListener('drop', onFileDrop);

  return () => {
    root.removeEventListener('dragover', onFileDragOver);
    root.removeEventListener('drop', onFileDrop);
  };
};
